import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { Capacitor } from "@capacitor/core";
import { getNonLoggedAuthId } from "src/api/getNonLoggedAuthId";
import { frontEnvString } from "src/services/runtimeFrontConfig";

// Same default as runtime-env seed REACT_APP_ANALYTICS_EXCLUDED_AUTH_IDS (used until front-config loads).
const DEFAULT_ANALYTICS_EXCLUDED_AUTH_IDS =
  "google-oauth2|118373356315210576839";

function isExcludedAnalyticsAuthId(authId: string): boolean {
  const id = (authId || "").trim();
  if (!id || id === "unknown") return false;
  const raw = frontEnvString("REACT_APP_ANALYTICS_EXCLUDED_AUTH_IDS", DEFAULT_ANALYTICS_EXCLUDED_AUTH_IDS);
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .includes(id);
}

// Helper function to check if tracking is enabled
const isTrackingEnabled = () => {
  // DISCOMMENT THE NEXT LINE TOreturn true for send eventso to FIREBEASE PROD (only for testing purposes)
  // return true;

  const prodBackendUrl = "https://chat-backend.rotogpt.com";

  // Check if the current environment's backend URL matches the production one
  const isProdEnvironment = process.env.REACT_APP_BACKEND_URL === prodBackendUrl;

  // Local/dev testing: send events to the Firebase project in firebase.ts (currently DEV).
  // Leave unset in prod builds so only isProdEnvironment can enable tracking.
  const forceEnabled = process.env.REACT_APP_ANALYTICS_FORCE_ENABLED === "true";
  // Log for easy debugging
  console.log(`[Analytics Check] Backend URL: ${process.env.REACT_APP_BACKEND_URL}`);
  console.log(`[Analytics Check] Is Production Environment? ${isProdEnvironment}`);
  console.log(`[Analytics Check] Force enabled? ${forceEnabled}`);

  return isProdEnvironment || forceEnabled;
};

const analyticsEnv = () => (process.env.REACT_APP_BACKEND_URL === "https://chat-backend.rotogpt.com" ? "prod" : "dev");

// Front-only flag sent to the backend to request a non-logged authId. Never send this in analytics.
const NON_LOGGED_AUTH_PLACEHOLDER = "non-logged-in-auth-id-rotogpt-2025";

let cachedNonLoggedAuthId: string | null = null;
let pendingNonLoggedAuthId: Promise<string> | null = null;

function isUnresolvedAnalyticsAuthId(authId: string): boolean {
  const id = authId.trim().toLowerCase();
  return !id || id === "unknown" || id === "anonymous" || id === NON_LOGGED_AUTH_PLACEHOLDER;
}

// Logged-in: Auth0 user.sub. Non-logged: IP+cookie id from /users/non-logged-auth-id (not the placeholder).
async function resolveAnalyticsAuthId(authId: string): Promise<string | null> {
  const trimmed = (authId || "").trim();
  // 1) Real Auth0 or already-resolved non-logged|ip|deviceId
  if (trimmed && !isUnresolvedAnalyticsAuthId(trimmed)) return trimmed;
  // 2) Guest: fetch real non-logged authId from backend (never placeholder)
  if (cachedNonLoggedAuthId) return cachedNonLoggedAuthId;
  if (!pendingNonLoggedAuthId) {
    pendingNonLoggedAuthId = getNonLoggedAuthId()
      .then(({ authId: real }) => {
        const resolved = (real || "").trim();
        if (!resolved || isUnresolvedAnalyticsAuthId(resolved)) {
          throw new Error("non-logged authId unresolved");
        }
        cachedNonLoggedAuthId = resolved;
        return resolved;
      })
      .finally(() => {
        pendingNonLoggedAuthId = null;
      });
  }
  try {
    return await pendingNonLoggedAuthId;
  } catch (error) {
    console.warn("[Analytics Plugin] Could not resolve non-logged authId:", error);
    return null;
  }
}

/** Auth0 → real non-logged|… → "unknown". null = team blocklist (do not send). Never sends the front placeholder. */
async function authIdForAnalytics(authId: string): Promise<string | null> {
  const resolved = (await resolveAnalyticsAuthId(authId)) || "unknown";
  if (isExcludedAnalyticsAuthId(resolved)) return null;
  return resolved;
}

// Shared logger for NEW funnel/timing events only. Existing track* helpers below stay unchanged.
const logAnalyticsEvent = async (name: string, params: Record<string, string | number>) => {
  if (!isTrackingEnabled()) return;
  try {
    const resolvedParams = { ...params };
    if (typeof resolvedParams.auth_id === "string") {
      const resolvedAuthId = await authIdForAnalytics(resolvedParams.auth_id);
      if (!resolvedAuthId) return;
      resolvedParams.auth_id = resolvedAuthId;
    }
    await FirebaseAnalytics.logEvent({
      name,
      params: {
        env: analyticsEnv(),
        ...resolvedParams,
      },
    });
    //console.log(`[Analytics Plugin] Tracked ${name}:`, resolvedParams);
  } catch (error) {
    console.error(`[Analytics Plugin] Error tracking ${name}:`, error);
  }
};

//Function for event tracking LOGIN INTO APP in mobile platform
export const trackAppLogin = async (authId: string, method: string, success: boolean) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "app_login",
      params: {
        auth_id: resolvedAuthId,
        method: method.trim().toLowerCase(),
        success: success ? "true" : "false",
      },
    });
    //console.log(`[Analytics Plugin] Tracked app login: ${authId}, ${method}, success: ${success}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking app login:", error);
  }
};

//function for event tracking LOGIN INTO APP ERROR in mobile platform
export const trackAppLoginError = async (authId: string, method: string, code: string, description?: string) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "app_login_error",
      params: {
        auth_id: resolvedAuthId,
        method: method.trim().toLowerCase(),
        code: (code || "unknown").slice(0, 60),
        description: (description || "").slice(0, 120),
      },
    });
    //console.log(`[Analytics Plugin] Tracked app login error for authId: ${authId}, code: ${code} | description: ${description} | method: ${method}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking app login error:", error);
  }
};

//Function for event tracking LOGIN PLATFORM in mobile platform
export const trackPlatformLogin = async (authId: string, platform: string, success: boolean) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "platform_login",
      params: {
        auth_id: resolvedAuthId,
        platform: platform.trim().toLowerCase(),
        success: success ? "true" : "false",
      },
    });
    //console.log(`[Analytics Plugin] Tracked platform login for authId: ${authId}, platform: ${platform}, success: ${success}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking platform login:", error);
  }
};

//function for event tracking LEAGUE SELECTION in mobile platform
export const trackLeagueSelection = async (authId: string, platformId: string, leagueId: string, teamName: string) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "league_selection",
      params: {
        auth_id: resolvedAuthId,
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
      },
    });
    //console.log(`[Analytics Plugin] Tracked league selection ${authId}, ${platformId} | ${leagueId} | ${teamName}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking league selection:", error);
  }
};

//function for event tracking MANAGED TEAMS SUMMARY in mobile platform
export const trackManagedTeamsSummary = async (authId: string, platformId: string, user_id: string, managedTeamsCount: number, totalTeams: number) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "managed_teams_summary",
      params: {
        auth_id: resolvedAuthId,
        platform_id: (platformId || "unknown").trim(),
        user_id: (user_id || "unknown").trim(),
        managed_teams_count: managedTeamsCount,
        total_teams_available: totalTeams,
      },
    });
    //console.log(`[Analytics Plugin] Tracked managed teams summary for ${authId}, platform_id ${platformId}, user_id ${user_id}: ${managedTeamsCount} of ${totalTeams}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking managed teams summary:", error);
  }
};

//function for event tracking TRADE ANALYSIS in mobile platform
export const trackTradeAnalysis = async (authId: string, platformId: string, leagueId: string, teamName: string) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "trade_analysis_executed",
      params: {
        auth_id: resolvedAuthId,
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
      },
    });
    //console.log(`[Analytics Plugin] Tracked trade analysis: ${authId}, ${platformId} | ${leagueId} | ${teamName}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking trade analysis:", error);
  }
};

// //function for event tracking ENDPOINTS EXECUTION in mobile platform
// export const trackEndpointsExecution = async (platformId: string, leagueId: string, teamName: string) => {
//   const isNative = Capacitor.isNativePlatform();
//   if (!isNative) {
//     //console.log("[Analytics] Skipping web platform - mobile only");
//     return;
//   }

//   if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
//     //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
//     return;
//   }

//   try {
//     await FirebaseAnalytics.logEvent({
//       name: "endpoints_execution",
//       params: {
//         platform_id: (platformId || "unknown").trim(),
//         league_id: (leagueId || "unknown").trim(),
//         team_name: (teamName || "unknown").trim(),
//       },
//     });
//     //console.log(`[Analytics Plugin] Tracked endpoints execution: ${platformId} | ${leagueId} | ${teamName}`);
//   } catch (error) {
//     console.error("[Analytics Plugin] Error tracking endpoints execution:", error);
//   }
// };

//function for event tracking when a specific endpoint is accessed
export const trackEndpointAccess = async (
  authId: string,
  platformId: string,
  leagueId: string,
  teamName: string,
  endpointName: "news" | "waiver" | "start" | "rosters" | "rankings" | "all_rankings" | "rundown" | "chatbot" | "draft_advice" | "articles",
) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "endpoint_accessed",
      params: {
        auth_id: resolvedAuthId,
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
        endpoint_name: endpointName,
      },
    });
    //console.log(`[Analytics Plugin] Tracked endpoint access for in  ${authId},platform ${platformId} | league ${leagueId} | team ${teamName} | endpoint ${endpointName}`);
  } catch (error) {
    console.error(`[Analytics Plugin] Error tracking endpoint access for ${endpointName}:`, error);
  }
};

//function for event tracking FEATURE TIME SPENT in mobile platform
export const trackFeatureTimeSpent = async (
  authId: string,
  featureName: "rundown" | "waiver" | "start" | "news" | "rankings" | "all_rankings" | "rosters" | "trades" | "chatbot" | "draft_advice" | "articles",
  timeSpentSeconds: number,
  platformId: string,
) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "feature_time_spent",
      params: {
        auth_id: resolvedAuthId,
        feature_name: featureName,
        time_spent_seconds: timeSpentSeconds,
        platform_id: (platformId || "unknown").trim(),
      },
    });
    //console.log(`[Analytics Plugin] Tracked feature time for ${authId}, ${platformId}: ${featureName} - ${timeSpentSeconds}s`);
  } catch (error) {
    console.error(`[Analytics Plugin] Error tracking feature time for ${featureName}:`, error);
  }
};

//function for event tracking SLOW OPERATIONS in mobile platform
export const trackSlowOperation = async (
  authId: string,
  operationName: "fetch_news" | "fetch_rankings" | "fetch_all_rankings" | "fetch_rosters" | "fetch_waiver" | "fetch_start" | "fetch_rundown" | "fetch_trade" | "fetch_draft_advice",
  durationSeconds: number,
  platformId: string,
  leagueId: string,
  teamName: string,
) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "slow_operation",
      params: {
        auth_id: resolvedAuthId,
        operation_name: operationName,
        duration_seconds: Math.round(durationSeconds), // Ensure it's an integer
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
      },
    });
    //console.log(`[Analytics Plugin] Tracked slow operation: ${authId}, ${platformId}, ${leagueId}, ${teamName}, ${operationName} - ${durationSeconds.toFixed(2)}s`);
  } catch (error) {
    console.error(`[Analytics Plugin] Error tracking slow operation for ${operationName}:`, error);
  }
};

//function for event tracking THUMBS FEEDBACK in mobile platform
export const trackThumbsFeedback = async (
  authId: string,
  section: "waiver" | "start" | "rundown" | "trade" | "draft_advice" | "chatbot" | "articles",
  feedback: "up" | "down",
  platformId: string,
  leagueId: string,
  teamName: string,
  messageId?: string,
  response?: string,
) => {
  if (!isTrackingEnabled()) return;

  const isNative = Capacitor.isNativePlatform();
  // if (!isNative) {
  //   //console.log("[Analytics] Skipping web platform - mobile only");
  //   return;
  // }

  // if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
  //   //console.log("[Analytics] Plugin FirebaseAnalytics NO available in native");
  //   return;
  // }

  try {
    const truncatedResponse = response ? response.substring(0, 100) : "";
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "thumbs_feedback",
      params: {
        auth_id: resolvedAuthId,
        section: section,
        feedback: feedback,
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
        message_id: (messageId || "unknown").trim(),
        response_snippet: truncatedResponse,
      },
    });
    //console.log(`[Analytics Plugin] Tracked thumbs feedback: ${authId}, ${section}, ${feedback}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking thumbs feedback:", error);
  }
};

// User sent a chatbot message (user_text over live WebSocket; voice is transcribed to text before send)
export const trackChatbotMessageSent = async (authId: string, platformId: string, leagueId: string, teamName: string) => {
  await logAnalyticsEvent("chatbot_message_sent", {
    auth_id: authId.trim() || "unknown",
    platform_id: (platformId || "unknown").trim(),
    league_id: (leagueId || "unknown").trim(),
    team_name: (teamName || "unknown").trim(),
  });
};

// Event tracking for audio mute toggle (mute action)
export const trackAudioMuted = async (authId: string, platformId: string, leagueId: string, teamName: string) => {
  if (!isTrackingEnabled()) return;

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "audio_muted",
      params: {
        auth_id: resolvedAuthId,
        status: "muted",
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
      },
    });
    //console.log(`[Analytics Plugin] Tracked audio muted: ${authId}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking audio muted:", error);
  }
};

// Event tracking for audio mute toggle (unmute action)
export const trackAudioUnmuted = async (authId: string, platformId: string, leagueId: string, teamName: string) => {
  if (!isTrackingEnabled()) return;

  try {
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "audio_unmuted",
      params: {
        auth_id: resolvedAuthId,
        status: "unmuted",
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
      },
    });
    //console.log(`[Analytics Plugin] Tracked audio unmuted: ${authId}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking audio unmuted:", error);
  }
};

// Event when user saves AI settings (audio, quan, etc.) from the AI Settings drawer
export const trackAiSettingsSaved = async (authId: string, platformId: string, leagueId: string, teamName: string, aiSettings: Record<string, boolean>, quanDefault?: boolean) => {
  if (!isTrackingEnabled()) return;

  try {
    const settingParams: Record<string, string> = {};
    Object.entries(aiSettings).forEach(([key, value]) => {
      if (key === "quanDefault") return;
      settingParams[`ai_setting_${key}`] = value ? "on" : "off";
    });
    const resolvedAuthId = await authIdForAnalytics(authId);
    if (!resolvedAuthId) return;
    await FirebaseAnalytics.logEvent({
      name: "ai_settings_saved",
      params: {
        auth_id: resolvedAuthId,
        platform_id: (platformId || "unknown").trim(),
        league_id: (leagueId || "unknown").trim(),
        team_name: (teamName || "unknown").trim(),
        ...settingParams,
        ...(quanDefault !== undefined ? { quan_default: quanDefault ? "on" : "off" } : {}),
      },
    });
    //console.log(`[Analytics Plugin] Tracked ai_settings_saved: ${authId}, ${platformId}, ${leagueId}, ${teamName}, ${JSON.stringify(aiSettings)}`);
  } catch (error) {
    console.error("[Analytics Plugin] Error tracking ai_settings_saved:", error);
  }
};

// Define the user tier types
export type UserTier = "anonymous" | "registered_free" | "paid_subscriber";

// Define the endpoint response name types
export type EndpointResponseName =
  | "common_league_data"
  | "super_analysis"
  | "trade"
  | "waiver"
  | "start"
  | "team_analysis"
  | "draft_advice"
  | "news"
  | "rankings"
  | "all_rankings"
  | "rosters";

// Stitch anonymous → authenticated journeys. Call with the same authId used in events.
export const setAnalyticsUser = async (
  authId: string,
  properties?: {
    user_tier?: UserTier;
    has_used_free_credit?: boolean;
    credits_remaining?: number;
  },
) => {
  if (!isTrackingEnabled()) return;
  try {
    const resolvedAuthId = await resolveAnalyticsAuthId(authId);
    if (!resolvedAuthId || isExcludedAnalyticsAuthId(resolvedAuthId)) return;
    await FirebaseAnalytics.setUserId({ userId: resolvedAuthId });
    if (properties?.user_tier) {
      await FirebaseAnalytics.setUserProperty({ key: "user_tier", value: properties.user_tier });
    }
    if (properties?.has_used_free_credit !== undefined) {
      await FirebaseAnalytics.setUserProperty({
        key: "has_used_free_credit",
        value: properties.has_used_free_credit ? "true" : "false",
      });
    }
    if (properties?.credits_remaining !== undefined) {
      await FirebaseAnalytics.setUserProperty({
        key: "credits_remaining",
        value: String(Math.max(0, Math.round(properties.credits_remaining))),
      });
    }
    if (resolvedAuthId.startsWith("non-logged")) {
      try {
        sessionStorage.setItem("analytics_anon_auth_id", resolvedAuthId);
      } catch {
        /* ignore */
      }
    }
    //console.log(`[Analytics Plugin] setUserId: ${resolvedAuthId}`, properties || {});
  } catch (error) {
    console.error("[Analytics Plugin] Error setting analytics user:", error);
  }
};

// Event tracking for instruction slides viewed.
// last_slide_seen only on skipped/completed (slide when closing/finishing); omit on started.
export const trackInstructionSlidesViewed = async (authId: string, action: "started" | "completed" | "skipped", lastSlideSeen?: number) => {
  const params: Record<string, string | number> = {
    auth_id: authId.trim() || "unknown",
    action,
  };
  if (lastSlideSeen != null && Number.isFinite(lastSlideSeen)) {
    params.last_slide_seen = Math.max(1, Math.round(lastSlideSeen));
  }
  await logAnalyticsEvent("instruction_slides_viewed", params);
};

// Event tracking for signup screen seen
export const trackSignupScreenSeen = async (
  authId: string,
  trigger: "free_limit_reached" | "manual_click" | "locked_feature",
  hadUsedFreeCredit: boolean,
  variant = "get_started",
) => {
  await logAnalyticsEvent("signup_screen_seen", {
    auth_id: authId.trim() || "unknown",
    trigger,
    variant,
    had_used_free_credit: hadUsedFreeCredit ? "true" : "false",
  });
};

// "anonymous" is a front placeholder; logAnalyticsEvent resolves IP+cookie / Auth0 id.
export const trackSignupScreenSeenAt = async (trigger: "free_limit_reached" | "manual_click" | "locked_feature", variant: string) => {
  await trackSignupScreenSeen("anonymous", trigger, getAnalyticsHadUsedFreeCredit(), variant);
};

// Welcome drawer closed without Create Account (guest CTA or dismiss outside drawer)
export const trackWelcomeDrawerDismissed = async (authId: string, action: "explore_as_guest" | "outside_click") => {
  await logAnalyticsEvent("welcome_drawer_dismissed", {
    auth_id: authId.trim() || "unknown",
    action,
  });
};

export const trackWelcomeDrawerDismissedAt = async (action: "explore_as_guest" | "outside_click") => {
  await trackWelcomeDrawerDismissed("anonymous", action);
};

// Event tracking for sign up
export const trackSignUp = async (authId: string, method: string, hadUsedFreeCredit: boolean, anonAuthId?: string) => {
  let anon = (anonAuthId || "").trim();
  // Never send front placeholder as anon_auth_id
  if (anon && isUnresolvedAnalyticsAuthId(anon)) {
    anon = (await resolveAnalyticsAuthId(anon)) || "none";
  }
  await logAnalyticsEvent("sign_up", {
    auth_id: authId.trim() || "unknown",
    method: (method || "unknown").trim().toLowerCase(),
    had_used_free_credit: hadUsedFreeCredit ? "true" : "false",
    anon_auth_id: anon || "none",
  });
};

// Event tracking for sign up intent
export const markAnalyticsSignupIntent = () => {
  try {
    sessionStorage.setItem("analytics_signup_intent", "true");
  } catch {
    /* ignore */
  }
};

// Event tracking for consume sign up intent
export const consumeAnalyticsSignupIntent = (): boolean => {
  try {
    const marked = sessionStorage.getItem("analytics_signup_intent") === "true";
    if (marked) sessionStorage.removeItem("analytics_signup_intent");
    return marked;
  } catch {
    return false;
  }
};

// Event tracking for get analytics had used free credit
export const getAnalyticsHadUsedFreeCredit = (): boolean => {
  try {
    return sessionStorage.getItem("analytics_had_used_free_credit") === "true";
  } catch {
    return false;
  }
};

// Event tracking for get analytics anon auth id
export const getAnalyticsAnonAuthId = (): string | undefined => {
  try {
    return sessionStorage.getItem("analytics_anon_auth_id") || undefined;
  } catch {
    return undefined;
  }
};

// Event tracking for plan duration from product id
export const planDurationFromProductId = (productId: string): string => {
  const id = (productId || "").toLowerCase();
  // IDs are league_connect_boost_* (not "booster") — one-time packs
  if (id.includes("boost")) return "booster";
  if (id.includes("annual")) return "annual";
  if (id.includes("season")) return "season";
  return "monthly";
};

// Event tracking for credit consumed
export const trackCreditConsumed = async (authId: string, currentBalance: number, isAuthenticated: boolean, featureUsed: string, creditType: "free" | "credits" | "bonus") => {
  if (!isAuthenticated) {
    try {
      sessionStorage.setItem("analytics_had_used_free_credit", "true");
    } catch {
      /* ignore */
    }
  }
  await logAnalyticsEvent("credit_consumed", {
    auth_id: authId.trim() || "unknown",
    current_balance: Math.max(0, Math.round(currentBalance)),
    is_authenticated: isAuthenticated ? "true" : "false",
    feature_used: (featureUsed || "unknown").slice(0, 40),
    credit_type: creditType,
  });
};

// Event tracking for paywall viewed
export const trackPaywallViewed = async (authId: string, trigger: "exceeded_free_credits" | "locked_feature_click" | "upgrade_button", planOptionsDisplayed: string) => {
  await logAnalyticsEvent("paywall_viewed", {
    auth_id: authId.trim() || "unknown",
    trigger,
    plan_options_displayed: (planOptionsDisplayed || "unknown").slice(0, 100),
  });
};

// Event tracking for begin checkout
export const trackBeginCheckout = async (authId: string, productId: string, planDuration: string, pricePoint: number, currency = "USD") => {
  await logAnalyticsEvent("begin_checkout", {
    auth_id: authId.trim() || "unknown",
    product_id: (productId || "unknown").slice(0, 40),
    product_type: (productId || "unknown").slice(0, 40),
    plan_duration: (planDuration || "unknown").slice(0, 40),
    price_point: Number.isFinite(pricePoint) ? pricePoint : 0,
    currency,
  });
};

// Event tracking for purchase
export const trackPurchase = async (authId: string, transactionId: string, productId: string, value: number, currency = "USD") => {
  await logAnalyticsEvent("purchase", {
    auth_id: authId.trim() || "unknown",
    transaction_id: (transactionId || "unknown").slice(0, 40),
    product_id: (productId || "unknown").slice(0, 40),
    value: Number.isFinite(value) ? value : 0,
    currency,
  });
};

// Event tracking for subscription failed
export const trackSubscriptionFailed = async (authId: string, productId: string, status: "failure" | "abandoned", errorCode: string) => {
  await logAnalyticsEvent("subscription_failed", {
    auth_id: authId.trim() || "unknown",
    product_id: (productId || "unknown").slice(0, 40),
    status,
    error_code: (errorCode || "unknown").slice(0, 60),
  });
};

// Event tracking for endpoint response time
export const trackEndpointResponseTime = async (authId: string, endpointName: EndpointResponseName, durationSeconds: number, platformId?: string, leagueId?: string) => {
  await logAnalyticsEvent("endpoint_response_time", {
    auth_id: (authId || "unknown").trim(),
    endpoint_name: endpointName,
    duration_seconds: Math.max(0, Math.round(durationSeconds)),
    platform_id: (platformId || "unknown").trim(),
    league_id: (leagueId || "unknown").trim(),
  });
};
